import Grid from '@material-ui/core/Grid';
import classNames from 'classnames';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';
import { ValueType } from 'react-select';

import Button from '~/ui/components/common/Button';
import Loader from '~/ui/components/common/Loader';
import Checkbox from '~/ui/components/inputs/Checkbox';
import Input from '~/ui/components/inputs/Input';
import Select from '~/ui/components/inputs/SelectWithoutAnimation';
import convertToUTC from '../../utils/convertToUTC';
import ObjectivesCheckboxGroup from '../ObjectivesCheckboxGroup';
import Testing from '../Testing';
import ClientNoShowFields from './components/ClientNoShowFields';
import VisitAddress from './components/VisitAddress';
import VisitDateSelectFields from './components/VisitDateSelectFields';
import VisitPersonSelectFields from './components/VisitPersonSelectFields';

import useClientMultiTeam from '~/hooks/useClientMultiTeamId';
import api from '~/services/api';
import { IVisitTreatmentPlan, PaperWorkStatus } from '~/services/api/clientDetails/types';
import { IDictionaryTypes } from '~/services/api/dictionaries/types';
import { IVisitForm, IVisitRequestPayload } from '~/services/api/visits/types';
import useFeature from '~/store/clinic/hooks/useFeature';
import { useStoreActions, useStoreState } from '~/store/hooks';
import useQuery from '~/store/medicationLogistics/hooks/useQuery';
import useRole from '~/store/user/hooks/useRole';
import { IOption } from '~/types';
import { PREVIEW_VISIT, VISITS } from '~/ui/constants/paths';
import formatClientAddress from '~/utils/dataFormat/formatClientAddress';
import { extractErrorMessage } from '~/utils/error/error';
import formatActTeamMemberOptions from '~/utils/formatActTeamMemberOptions';
import formatDictionaryOptions from '~/utils/formatDictionaryOptions';
import formatObjectivesOptions from '~/utils/formatObjectivesOptions';
import addressAdditionalOption from '../../constants/addressAdditionalOption';
import { visitTypes } from '../../constants/filterOptions';
import formatToxicologyPayload from '../../utils/formatToxicologyPayload';
import getSeconds from '../../utils/getSeconds';
import getSelectedObjectives from '../../utils/getSelectedObjectives';
import isStatusDone from '../../utils/isStatusDone';
import validation, { NOTE_MAX_LENGTH } from './validation';

import styles from './Form.module.scss';

interface IProps {
  visitId: string;
  defaultValues: IVisitForm | null;
  fromPreview?: boolean;
  clientNoShow?: boolean;
  treatmentPlan?: IVisitTreatmentPlan;
  paperworkStatus?: PaperWorkStatus;
}

const Form = ({
  visitId,
  defaultValues,
  fromPreview,
  paperworkStatus,
  treatmentPlan,
  clientNoShow: initialClientNoShow,
}: IProps): ReactElement => {
  const initialObjectives = getSelectedObjectives(treatmentPlan);

  const [loading, setLoading] = useState<boolean>(true);
  const [noShowReasons, setNoShowReasons] = useState<IOption[]>([]);
  const [interventions, setInterventions] = useState<IOption[]>([]);
  const [teamMembers, setTeamMembers] = useState<IOption[]>([]);
  const [objectives, setObjectives] = useState<IOption[]>(initialObjectives);
  const [states, setStates] = useState<IOption[]>([]);
  const navigate = useNavigate();

  const { availableActTeams } = useStoreState(state => state.actTeam);

  const query = useQuery();
  const teamIdQuery = query.get('teamId');
  const clientId = query.get('clientId');

  const { current } = useStoreState(state => state.user);
  const { current: client } = useStoreState(state => state.client);
  const { addresses } = useStoreState(state => state.clientDetails);
  const { current: visit } = useStoreState(state => state.visits);

  const { onGetClient } = useStoreActions(action => action.client);
  const { onAddVisit, onEditVisit } = useStoreActions(action => action.visits);
  const { onGetAddresses } = useStoreActions(actions => actions.clientDetails);
  const { showError } = useStoreActions(action => action.snackbar);
  const { onGetActTeamsAvailable } = useStoreActions(action => action.actTeam);
  const { onGetTreatmentPlans } = useStoreActions(action => action.clientDetails);

  const {
    isTreatmentPlansAvailable,
    isBidirectionalAvailable,
    isToxicologyAvailable,
    isInverventionsAvailable,
  } = useFeature();
  const { isGlobalUser, isLocalAdmin, isProgramAssistant } = useRole();

  const { getClientMultiTeamId } = useClientMultiTeam();
  const clientTeamId = getClientMultiTeamId(client?.actTeams);

  const form = useForm({
    resolver: validation,
    mode: 'onChange',
    defaultValues: {
      ...defaultValues,
      billable: defaultValues ? defaultValues.billable : true,
      sendToEmr: defaultValues ? defaultValues.sendToEmr : true,
      clientNoShow: defaultValues ? defaultValues.clientNoShow : initialClientNoShow,
      address: 0,
      duration: defaultValues?.duration || '',
      teamId: Number(teamIdQuery) || clientTeamId,
    },
  });
  const {
    control,
    register,
    unregister,
    watch,
    handleSubmit,
    setValue,
    clearErrors,
    formState: { errors, dirtyFields, isDirty },
  } = form;

  const { type, clientNoShow, billable, toxicology, bloodDrawn, teamId, note } = watch();

  const visitedPersonsList = useMemo((): IOption[] => {
    if (client) {
      const array = [
        { label: `${client.firstName} ${client.lastName} | Client`, value: client.id },
        { label: 'Hospital Staff', value: 0 },
        { label: 'AOT Contact', value: -1 },
      ];
      client.supportContacts.forEach(contact => {
        if (!contact.isArchived) {
          array.push({
            label: `${contact.firstName} ${contact.lastName} | ${contact.relationship.name}`,
            value: contact.id,
          });
        }
      });
      return array;
    }
    return [];
  }, [client]);

  const dispositionOptions = useMemo(
    () => [
      { label: 'Pending', value: 1 },
      { label: 'Assign Clinician for Follow-up', value: 2 },
      { label: 'Crisis Resolved', value: 3 },
      { label: 'Referral to Hospital', value: 4 },
      { label: 'Pre-Admission Screening', value: 5 },
    ],
    [],
  );

  const onCancel = (): void => {
    if (fromPreview) {
      navigate(
        PREVIEW_VISIT.replace(':visitId', String(visitId)).concat(
          `?teamId=${teamId}&clientId=${clientId}&isCrisisVisits=${visit?.isCrisis ?? false}`,
        ),
      );
    } else {
      navigate(VISITS.concat(`?teamId=${teamId}&clientId=${clientId}`));
    }
  };

  const onSubmit = async (values: IVisitForm) => {
    const {
      type: typeOfVisit,
      visitedPeople,
      date,
      startTime,
      line1,
      city,
      stateId,
      line2,
      zipCode,
      duration,
      noShowReason,
      bloodDrawn: bd,
      toxicology: tx,
      billable: isBillable,
      sendToEmr,
      address: addressId,
      ...rest
    } = values;

    // if address entered manually
    let visitAddress =
      line1 && stateId ? { line1, line2: defaultValues?.line2, stateId, city, zipCode } : null;

    // if address selected from the list
    if (addressId) {
      const selectedAddress = addresses.find(item => item.id === addressId);
      if (selectedAddress) {
        visitAddress = {
          line1: selectedAddress.line1,
          line2: selectedAddress.line2,
          stateId: selectedAddress.state.id,
          city: selectedAddress.city,
          zipCode: selectedAddress.zipCode,
        };
      }
    }

    const requestPayload: IVisitRequestPayload = {
      type: Object.keys(visitTypes)[Number(typeOfVisit)],
      durationSeconds: !clientNoShow ? getSeconds(duration) : null,
      date,
      startTime: startTime.toString().split(' ')[4],
      utcStartTime: convertToUTC(date, startTime),
      clientId: Number(clientId),
      isClientVisited: !clientNoShow ? visitedPeople.includes(client.id) : false,
      isHospitalStaffVisited: !clientNoShow ? visitedPeople.includes(0) : false,
      isAotContactVisited: !clientNoShow ? visitedPeople.includes(-1) : false,
      visitedPeople: !clientNoShow
        ? visitedPeople.filter(person => person > 0 && person !== client.id && person)
        : null,
      address: visitAddress,
      noShowReason: clientNoShow ? noShowReason : null,
      paperworkStatus: paperworkStatus || rest.paperworkStatus,
      treatmentPlanDetails:
        treatmentPlan?.goals.length && isTreatmentPlansAvailable
          ? {
              id: treatmentPlan.id,
              reviewedObjectives: objectives.map(item => Number(item.value)),
            }
          : null,
      toxicologyResults:
        clientNoShow || !isToxicologyAvailable
          ? null
          : formatToxicologyPayload({ toxicology: tx, bloodDrawn: bd }),
      billable: clientNoShow ? false : !!isBillable,
      sendToEmr: isBidirectionalAvailable ? sendToEmr : false,
      ...rest,
    };

    try {
      if (!defaultValues) {
        await onAddVisit({
          requestInfo: { clinicId: String(current?.clinic?.id), teamId: String(teamId) },
          requestPayload,
        });
      } else {
        await onEditVisit({
          requestInfo: { clinicId: String(current?.clinic?.id), teamId: String(teamId), visitId },
          requestPayload,
        });
      }
      onCancel();
    } catch (e) {
      showError(extractErrorMessage(e));
    }
  };

  const hasDefaultValues = useMemo(() => !!defaultValues, [defaultValues]);

  const onMount = useCallback(async () => {
    if (current?.clinic?.id && teamId && clientId) {
      try {
        setLoading(true);

        if (!availableActTeams.length && (isLocalAdmin || isProgramAssistant || isGlobalUser)) {
          onGetActTeamsAvailable({ clinicId: String(current?.clinic?.id) });
        }

        await onGetClient({
          clinicId: String(current?.clinic?.id),
          teamId: String(teamId),
          clientId,
        });

        await onGetAddresses({
          clinicId: String(current?.clinic?.id),
          teamId: String(teamId),
          clientId,
        });

        const { data: members } = await api.actTeamMember.getActTeamMemberList(
          String(current?.clinic?.id),
          String(teamId),
        );
        setTeamMembers(formatActTeamMemberOptions(members, !hasDefaultValues));

        const { data: reasons } = await api.dictionaries.getAvailableTypeList(
          IDictionaryTypes.VisitNoShowReason,
        );
        const { data: interventionsDictionary } = await api.dictionaries.getAvailableTypeList(
          IDictionaryTypes.Intervention,
        );
        setInterventions(formatDictionaryOptions(interventionsDictionary));
        setNoShowReasons(formatDictionaryOptions(reasons));
        const { data: dictionaryStates } = await api.dictionaries.getAvailableTypeList(
          IDictionaryTypes.State,
        );
        setStates(formatDictionaryOptions(dictionaryStates));
      } catch (e) {
        showError(extractErrorMessage(e));
      } finally {
        setLoading(false);
      }
    }
  }, [
    clientId,
    current?.clinic?.id,
    hasDefaultValues,
    onGetAddresses,
    onGetClient,
    showError,
    teamId,
    isLocalAdmin,
    isProgramAssistant,
    isGlobalUser,
    onGetActTeamsAvailable,
    availableActTeams,
  ]);

  useEffect(() => {
    onMount();
  }, [visitId, onMount]);

  const isPersonVisit = type === 0 || type === 1 || visit?.isCrisis;

  useEffect(() => {
    if (!clientNoShow) {
      setValue('noShowReason', null);
    }
    if (!isPersonVisit) {
      unregister(['stateId', 'zipCode', 'line1', 'city']);
    }
  }, [clientNoShow, isPersonVisit, setValue, unregister]);

  useEffect(() => {
    if (clientNoShow) {
      // reset toxicologyResults if client no show
      unregister([
        'toxicology.status',
        'toxicology.result',
        'toxicology.date',
        'bloodDrawn.status',
        'bloodDrawn.drawDate',
      ]);
    }
  }, [clientNoShow, unregister]);

  // reset testing data if status not Done
  useEffect(() => {
    if (!isStatusDone(toxicology?.status)) {
      setValue('toxicology.result', null);
      setValue('toxicology.date', null);
      clearErrors(['toxicology.result', 'toxicology.date']);
    } else if (!isStatusDone(bloodDrawn?.status)) {
      setValue('bloodDrawn.drawDate', null);
      clearErrors(['bloodDrawn.drawDate']);
    }
  }, [toxicology?.status, bloodDrawn?.status, setValue, clearErrors]);

  const filteredMembers = useMemo(
    () =>
      teamMembers.filter(
        member => !member.isDisabled || defaultValues?.visitors.includes(member.value as number),
      ),
    [teamMembers, defaultValues],
  );

  const filteredAddresses = useMemo(() => {
    const newAddresses = addresses.reduce((acc, obj) => {
      if (!obj.isArchived) {
        acc.push({ value: obj.id, label: formatClientAddress(obj) });
      }

      return acc;
    }, []);

    newAddresses.unshift(addressAdditionalOption);

    return newAddresses;
  }, [addresses]);

  const actTeamOptions = useMemo(() => {
    if (!client?.actTeams || !availableActTeams)
      return client?.actTeams.map(actTeam => ({
        value: actTeam.team.id,
        label: actTeam.isPrimary ? `${actTeam.team.name} (Primary)` : actTeam.team.name,
      }));

    const availableTeamIds = availableActTeams.map(team => team.id);

    return client.actTeams
      .filter(actTeam => !actTeam.isArchived && availableTeamIds.includes(actTeam.team.id))
      .map(actTeam => ({
        value: actTeam.team.id,
        label: actTeam.isPrimary ? `${actTeam.team.name} (Primary)` : actTeam.team.name,
      }));
  }, [availableActTeams, client?.actTeams]);

  const [visitors, visitedPeople, addressWatchValue] = watch([
    'visitors',
    'visitedPeople',
    'address',
  ]);

  useEffect(() => {
    // effect in order to update fields on change team
    if (dirtyFields.teamId) {
      setValue(
        'visitors',
        filteredMembers
          .filter(member => visitors?.includes(Number(member.value)))
          .map(member => Number(member.value)),
      );
      if (!clientNoShow) {
        setValue(
          'visitedPeople',
          visitedPersonsList
            .filter(person => visitedPeople?.includes(Number(person.value)))
            .map(person => Number(person.value)),
        );
      }
      if (isPersonVisit) {
        setValue(
          'address',
          filteredAddresses?.find(a => a.value === addressWatchValue)?.value || null,
        );
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredAddresses, visitedPersonsList, filteredMembers, dirtyFields]);

  const handleSelectTeam = (option: ValueType<IOption | IOption[], boolean>) => {
    // refetch available treatment plans on change team
    if (isTreatmentPlansAvailable && (option as IOption).value !== teamId) {
      onGetTreatmentPlans({
        clinicId: String(current?.clinic?.id),
        teamId: String((option as IOption).value),
        clientId,
      });
    }
  };

  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        {loading && <Loader className={styles.loader} />}
        <Grid
          container
          spacing={2}
          className={classNames(styles.form, { [styles.loading]: loading })}
        >
          <Grid item sm={12}>
            <h4>Main information</h4>
          </Grid>
          {visit?.isCrisis && (
            <Grid item sm={12} className={styles.crisisVisit}>
              <Checkbox
                control={control}
                errors={errors}
                name="isCrisis"
                label="Crisis Visit"
                disabled
              />
            </Grid>
          )}
          {isGlobalUser && (
            <Grid item sm={3} className={classNames({ [styles.disabled]: !!visitId })}>
              <Select
                control={control}
                errors={errors}
                name="teamId"
                options={actTeamOptions}
                label="Select Team"
                hideSelectedOptions={false}
                openMenuOnFocus
                isDisabled={!!visitId}
                onSelect={handleSelectTeam}
              />
            </Grid>
          )}
          <VisitDateSelectFields clientNoShow={clientNoShow} />
          <VisitPersonSelectFields
            clientNoShow={clientNoShow}
            visitedPersonsList={visitedPersonsList}
            filteredMembers={filteredMembers}
          />
          {!clientNoShow && isToxicologyAvailable && (
            <Grid container item sm={9}>
              <Testing
                control={control}
                errors={errors}
                toxicology={toxicology}
                bloodDrawn={bloodDrawn}
              />
            </Grid>
          )}
          <Grid item sm={9}>
            <Input
              register={register}
              errors={errors}
              name="note"
              label="Add Note"
              multiline
              minRows={4}
              helperText={
                note?.length && note.length <= NOTE_MAX_LENGTH
                  ? `${note.length.toLocaleString()} / ${NOTE_MAX_LENGTH.toLocaleString()} characters`
                  : ''
              }
            />
          </Grid>
          {isBidirectionalAvailable && (
            <Grid item sm={3}>
              <Checkbox
                control={control}
                errors={errors}
                name="sendToEmr"
                label="Send to EHR"
                disabled={defaultValues?.sendToEmr}
              />
            </Grid>
          )}
          {isPersonVisit && <VisitAddress states={states} />}
          {visit?.isCrisis && (
            <>
              <Grid item sm={9}>
                <Select
                  control={control}
                  errors={errors}
                  name="disposition"
                  options={dispositionOptions}
                  label="Disposition"
                  isMulti
                  hideSelectedOptions={false}
                  openMenuOnFocus
                />
              </Grid>
            </>
          )}
          <ClientNoShowFields
            clientNoShow={clientNoShow}
            noShowReasons={noShowReasons}
            isEdit={!!defaultValues}
          />
          {!!treatmentPlan?.goals?.length && !clientNoShow && isTreatmentPlansAvailable && (
            <Grid container item sm={9} spacing={2}>
              <Grid item sm={12}>
                <h4>Objectives Reviewed</h4>
              </Grid>
              {treatmentPlan?.goals.map(goal => (
                <Grid item sm={12} key={`${goal.text}-${goal.id}`}>
                  <ObjectivesCheckboxGroup
                    title={goal.text}
                    options={formatObjectivesOptions(goal.objectives)}
                    selected={objectives}
                    setSelected={setObjectives}
                  />
                </Grid>
              ))}
            </Grid>
          )}

          {!clientNoShow && (
            <Grid container item sm={3} spacing={2}>
              <Grid item sm={12}>
                <Checkbox
                  control={control}
                  errors={errors}
                  name="billable"
                  label="Billable Visit"
                />
              </Grid>
              {isInverventionsAvailable && billable && (
                <Grid item sm={12}>
                  <Select
                    control={control}
                    errors={errors}
                    name="interventionIds"
                    options={interventions}
                    label="Interventions"
                    isMulti
                    hideSelectedOptions={false}
                    openMenuOnFocus
                    isNarrow
                  />
                </Grid>
              )}
            </Grid>
          )}
        </Grid>

        <Grid container item sm={12} spacing={2} className={styles.buttonHolder}>
          <Grid item>
            <Button color="primary" variant="outlined" onClick={onCancel}>
              Cancel
            </Button>
          </Grid>
          <Grid item>
            <Button
              disabled={!isDirty || Object.keys(errors).length > 0}
              color="primary"
              variant="contained"
              type="submit"
            >
              {visit && visitId ? 'Update' : 'Save'}
            </Button>
          </Grid>
        </Grid>
      </form>
    </FormProvider>
  );
};

export default Form;
